import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  MagnifyingGlass, Sparkle, MapPin, TrendUp, Clock, Truck,
  ArrowRight, Warning, CheckCircle, Envelope, Star, Storefront, X, PaperPlaneTilt, PencilSimple,
  Trophy, Handshake, Copy, Phone, Lightning, Hourglass, Bell, BellRinging,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useNavigate, Link } from "react-router-dom";
import LanePricingCard from "../components/LanePricingCard";
import RepeatBidsAlert from "../components/RepeatBidsAlert";
import LoadBoardWatchersCard from "../components/LoadBoardWatchersCard";

const EQUIPMENT = [
  { value: "van", label: "Dry Van" },
  { value: "reefer", label: "Reefer" },
  { value: "flatbed", label: "Flatbed" },
  { value: "power_only", label: "Power Only" },
];

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS",
  "KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY",
  "NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV",
  "WI","WY",
];

const TIER_STYLE = {
  strong:   { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-400", label: "STRONG" },
  good:     { bg: "bg-[#FF5A1F]/15",   border: "border-[#FF5A1F]/40",   text: "text-[#FF5A1F]",   label: "GOOD"   },
  marginal: { bg: "bg-amber-500/15",   border: "border-amber-400/40",   text: "text-amber-400",   label: "MARGINAL" },
  skip:     { bg: "bg-white/5",        border: "border-white/10",        text: "text-neutral-500", label: "SKIP"   },
};

const fmtMoney = (n) => `$${Math.round(n).toLocaleString()}`;
const fmtAge = (mins) => {
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};


export default function LoadBoardPage() {
  const navigate = useNavigate();
  const [providerStatus, setProviderStatus] = useState(null);
  const [criteria, setCriteria] = useState({
    origin_state: "",
    origin_city: "",
    destination_state: "",
    destination_city: "",
    equipment: "van",
    min_rate_per_mile: "",
    max_deadhead_miles: "",
    limit: 25,
  });
  const [results, setResults] = useState([]);
  const [repeatFlags, setRepeatFlags] = useState({});  // {"broker|OS|DS": {alert, count, won, lost}}
  const [replySpeed, setReplySpeed] = useState({});    // {broker_company: {avg_reply_hours, replies, win_rate_pct}}
  const [speedList, setSpeedList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [biddingIds, setBiddingIds] = useState(new Set());
  const [draftModal, setDraftModal] = useState(null);   // {match, draft: {subject, body, target_rate}, sending, bidId}

  // Load provider status once so we can show the DEMO MODE banner
  useEffect(() => {
    client.get("/loadboard/status").then((r) => setProviderStatus(r.data)).catch(() => {});
    client.get("/loadboard/broker-reply-speed").then((r) => {
      const list = r.data.brokers || [];
      setSpeedList(list);
      setReplySpeed(Object.fromEntries(list.map((b) => [b.broker_company, b])));
    }).catch(() => {});
  }, []);

  const runSearch = async () => {
    setLoading(true);
    setRepeatFlags({});
    try {
      const body = {
        equipment: criteria.equipment,
        origin_state: criteria.origin_state || undefined,
        origin_city: criteria.origin_city.trim() || undefined,
        destination_state: criteria.destination_state || undefined,
        destination_city: criteria.destination_city.trim() || undefined,
        min_rate_per_mile: criteria.min_rate_per_mile ? parseFloat(criteria.min_rate_per_mile) : undefined,
        max_deadhead_miles: criteria.max_deadhead_miles ? parseInt(criteria.max_deadhead_miles, 10) : undefined,
        limit: criteria.limit,
      };
      const r = await client.post("/loadboard/search", body);
      const matches = r.data.results || [];
      setResults(matches);
      setHasSearched(true);
      if (!matches.length) toast.info("No matches. Try loosening the filters.");
      // Batch-fetch repeat-bid flags for every unique (broker, lane) combo in
      // the results — one API call regardless of result count
      if (matches.length > 0) {
        const combos = Array.from(new Map(matches.map((m) => [
          `${m.broker_company}|${m.origin_state}|${m.destination_state}`,
          {
            broker_company: m.broker_company,
            origin_state: m.origin_state,
            destination_state: m.destination_state,
          },
        ])).values());
        client.post("/loadboard/repeat-bids-flags", { combos, days: 7, min_repeat: 3 })
          .then((res) => setRepeatFlags(res.data.flags || {}))
          .catch(() => setRepeatFlags({}));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const bidOnMatch = async (m) => {
    if (biddingIds.has(m.match_id)) return;
    setBiddingIds((prev) => new Set(prev).add(m.match_id));
    try {
      // Step 1: Draft the negotiation email
      const negotiate = await client.post("/dispatch/negotiate-rate", {
        load_number: m.provider_load_id,
        customer: m.broker_company,
        origin: `${m.origin_city}, ${m.origin_state}`,
        destination: `${m.destination_city}, ${m.destination_state}`,
        pickup_date: m.pickup_date,
        delivery_date: m.delivery_date,
        posted_rate: m.posted_rate,
        target_rate: m.ai_target_rate,
        broker_name: m.broker_contact,
        broker_email: m.broker_email,
        reasons: [
          "fuel",
          ...(m.deadhead_miles > 50 ? ["deadhead"] : []),
        ],
        notes: `Weight: ${m.weight_lbs} lbs · Commodity: ${m.commodity} · Equipment: ${m.equipment}`,
      });

      // Step 2: Record the bid for analytics
      const bid = await client.post(`/loadboard/loads/${m.match_id}/bid`, {
        match_id: m.match_id,
        posted_rate: m.posted_rate,
        ai_target_rate: m.ai_target_rate,
        bid_amount: negotiate.data.target_rate || m.ai_target_rate,
        broker_company: m.broker_company,
        origin: `${m.origin_city}, ${m.origin_state}`,
        destination: `${m.destination_city}, ${m.destination_state}`,
        miles: m.miles,
        equipment: m.equipment,
        provider: m.provider || "",
      });

      // Step 3: Open review modal so user sees the drafted email
      setDraftModal({
        match: m,
        draft: {
          subject: negotiate.data.subject || `Rate discussion — ${m.origin_city}, ${m.origin_state} → ${m.destination_city}, ${m.destination_state}`,
          body: negotiate.data.body || "",
          target_rate: negotiate.data.target_rate,
          anchor_rate: negotiate.data.anchor_rate,
          rejection_history: negotiate.data.rejection_history,
          wins_history: negotiate.data.wins_history,
          broker_risk: negotiate.data.broker_risk,
        },
        bidId: bid.data.id,
        sending: false,
      });
      toast.success("Bid drafted — review below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bid draft failed");
    } finally {
      setBiddingIds((prev) => {
        const next = new Set(prev);
        next.delete(m.match_id);
        return next;
      });
    }
  };

  const sendDraft = async () => {
    if (!draftModal) return;
    setDraftModal((p) => ({ ...p, sending: true }));
    try {
      await client.post("/dispatch/send-broker-email", {
        to_email: draftModal.match.broker_email,
        subject: draftModal.draft.subject,
        body: draftModal.draft.body,
        load_number: draftModal.match.provider_load_id,
      });
      toast.success(`Email sent to ${draftModal.match.broker_email}`);
      setDraftModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — connect Gmail SMTP in Settings for better deliverability");
      setDraftModal((p) => ({ ...p, sending: false }));
    }
  };

  const stats = useMemo(() => {
    if (!results.length) return null;
    const strongCount = results.filter((r) => r.ai_tier === "strong").length;
    const avgScore = results.reduce((s, r) => s + r.ai_score, 0) / results.length;
    const avgPerMi = results.reduce((s, r) => s + r.rate_per_mile, 0) / results.length;
    return {
      total: results.length,
      strong: strongCount,
      avgScore: Math.round(avgScore),
      avgPerMi: avgPerMi.toFixed(2),
    };
  }, [results]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="loadboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Storefront size={22} className="text-[#FF5A1F]" weight="bold" />
            <div className="overline text-[#FF5A1F]">LOAD BOARD</div>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Find loads. AI ranks them.
          </h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-xl">
            Every match is scored against YOUR rate rules — so you spend time on the best ones,
            not the ones with the flashiest posted rate.
          </p>
        </div>
        {providerStatus && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to="/app/loadboard/analytics"
              className="text-xs text-neutral-400 hover:text-white underline underline-offset-2"
              data-testid="loadboard-analytics-link"
            >
              View bid analytics →
            </Link>
            <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
              providerStatus.live
                ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                : "border-amber-400/30 bg-amber-400/10 text-amber-300"
            }`} data-testid="loadboard-provider-badge">
              <span className={`w-1.5 h-1.5 rounded-full ${providerStatus.live ? "bg-emerald-400" : "bg-amber-400"}`} />
              {providerStatus.live ? "LIVE" : "DEMO MODE"} · {providerStatus.display_name}
            </div>
          </div>
        )}
      </div>

      {/* Demo-mode explainer (only when not live) */}
      {providerStatus && !providerStatus.live && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] p-4 flex items-start gap-3" data-testid="loadboard-demo-banner">
          <Warning size={20} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-neutral-300 leading-relaxed">
            <b className="text-amber-300">Practice loads — not live freight.</b> Every load below is
            realistic <b className="text-amber-200">sample data</b> so you can master the full workflow
            (AI scoring, one-click bid drafts, lane alerts) risk-free. Have a DAT subscription?
            Connect it below and this board switches to <b className="text-emerald-300">live DAT loads</b> —
            your saved lanes and settings carry over.
          </div>
        </div>
      )}

      {/* Connect DAT account — per-company live board hookup */}
      <DATConnectCard live={!!providerStatus?.live} onChanged={() => window.location.reload()} />
      <TruckstopConnectCard onChanged={() => window.location.reload()} />

      {/* Post-a-load promo — grow the FreightPilot marketplace */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 flex flex-wrap items-center gap-2 text-xs text-neutral-400" data-testid="post-load-promo">
        <span>📦 Know a shipper or owner-op with freight to move?</span>
        <a href="/post-load" target="_blank" rel="noreferrer" className="text-[#FF9764] font-semibold hover:underline" data-testid="post-load-promo-link">
          They can post it free at freightpilotai.ai/post-load
        </a>
        <span>— it shows here marked <b className="text-emerald-400">● LIVE</b> the second they hit post.</span>
      </div>

      {/* Reply Rate Tracker — % of sent bids that get answered */}
      <HotStreakBadge />
      <ReplyRateCard />

      {/* Fastest Brokers — ranked by how quickly they answer bids */}
      {speedList.length > 0 && <FastestBrokersCard brokers={speedList} />}

      {/* Live watchers — pilot's saved broker+lane alerts */}
      <LoadBoardWatchersCard />

      {/* Search form */}
      <div className="card-surface p-5 sm:p-6" data-testid="loadboard-search-form">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          <div>
            <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Origin state</label>
            <select
              value={criteria.origin_state}
              onChange={(e) => setCriteria((p) => ({ ...p, origin_state: e.target.value }))}
              data-testid="search-origin-state"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
            >
              <option value="">Any</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Origin city</label>
            <input
              type="text"
              value={criteria.origin_city}
              onChange={(e) => setCriteria((p) => ({ ...p, origin_city: e.target.value }))}
              placeholder="Any city"
              data-testid="search-origin-city"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Destination state</label>
            <select
              value={criteria.destination_state}
              onChange={(e) => setCriteria((p) => ({ ...p, destination_state: e.target.value }))}
              data-testid="search-destination-state"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
            >
              <option value="">Any</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Destination city</label>
            <input
              type="text"
              value={criteria.destination_city}
              onChange={(e) => setCriteria((p) => ({ ...p, destination_city: e.target.value }))}
              placeholder="Any city"
              data-testid="search-destination-city"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Equipment</label>
            <select
              value={criteria.equipment}
              onChange={(e) => setCriteria((p) => ({ ...p, equipment: e.target.value }))}
              data-testid="search-equipment"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
            >
              {EQUIPMENT.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Min $/mile</label>
            <input
              type="number"
              step="0.05"
              value={criteria.min_rate_per_mile}
              onChange={(e) => setCriteria((p) => ({ ...p, min_rate_per_mile: e.target.value }))}
              placeholder="2.30"
              data-testid="search-min-per-mile"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Max deadhead</label>
            <input
              type="number"
              value={criteria.max_deadhead_miles}
              onChange={(e) => setCriteria((p) => ({ ...p, max_deadhead_miles: e.target.value }))}
              placeholder="100"
              data-testid="search-max-deadhead"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
            />
          </div>
          <div className="flex flex-col justify-end">
            <button
              onClick={runSearch}
              disabled={loading}
              data-testid="search-run-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#FF5A1F] text-black font-semibold py-2.5 text-sm hover:bg-[#FF7038] transition-colors disabled:opacity-50"
            >
              <MagnifyingGlass size={16} weight="bold" />
              {loading ? "Searching…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      {/* Result stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="loadboard-stats">
          <StatCard icon={<Storefront size={14} />} label="Matches" value={stats.total} />
          <StatCard icon={<Star size={14} weight="fill" />} label="Strong picks" value={stats.strong} tone="good" />
          <StatCard icon={<TrendUp size={14} />} label="Avg score" value={stats.avgScore} tone="money" />
          <StatCard icon={<Truck size={14} />} label="Avg $/mi" value={`$${stats.avgPerMi}`} tone="money" />
        </div>
      )}

      {/* Lane pricing intelligence — user's own P25/avg/P75 for this corridor */}
      <LanePricingCard
        originState={criteria.origin_state}
        destState={criteria.destination_state}
        equipment={criteria.equipment}
      />

      {/* Results list */}
      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500 text-sm" data-testid="loadboard-loading">
          Searching…
        </div>
      ) : hasSearched && results.length === 0 ? (
        <div className="card-surface p-8 text-center" data-testid="loadboard-empty">
          <MagnifyingGlass size={32} className="text-neutral-600 mx-auto mb-2" />
          <div className="text-sm text-neutral-400">No matches with your criteria. Try loosening the min $/mile or picking different states.</div>
        </div>
      ) : results.length > 0 ? (
        <div className="space-y-3" data-testid="loadboard-results">
          {results.map((m) => {
            const flagKey = `${m.broker_company}|${m.origin_state}|${m.destination_state}`;
            return (
              <MatchRow
                key={m.match_id}
                match={m}
                onBid={bidOnMatch}
                isBidding={biddingIds.has(m.match_id)}
                repeatFlag={repeatFlags[flagKey]}
                brokerSpeed={replySpeed[m.broker_company]}
              />
            );
          })}
        </div>
      ) : (
        <div className="card-surface p-8 text-center" data-testid="loadboard-idle">
          <Storefront size={40} className="text-neutral-600 mx-auto mb-3" />
          <div className="text-sm text-neutral-400">Pick your lane above and hit Search.</div>
          <div className="text-xs text-neutral-600 mt-2">Every match is scored 0-100 against your Rate Rules.</div>
        </div>
      )}

      {/* Draft Bid review modal */}
      {draftModal && (
        <DraftBidModal
          state={draftModal}
          onUpdateBody={(body) => setDraftModal((p) => ({ ...p, draft: { ...p.draft, body } }))}
          onUpdateSubject={(subject) => setDraftModal((p) => ({ ...p, draft: { ...p.draft, subject } }))}
          onSend={sendDraft}
          onClose={() => setDraftModal(null)}
        />
      )}
    </div>
  );
}


// Connect DAT Account — per-company credentials, verified live on save.
function DATConnectCard({ live, onChanged }) {
  const [conn, setConn] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ service_email: "", service_password: "", seat_user_email: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await client.get("/loadboard/dat/connection");
      setConn(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!form.service_email || !form.service_password || !form.seat_user_email) {
      toast.error("All three fields are needed");
      return;
    }
    setBusy(true);
    try {
      const { data } = await client.post("/loadboard/dat/connect", form);
      if (data.connected) {
        toast.success("DAT connected — your board is now LIVE 🎉");
        onChanged();
      } else {
        toast.error(`DAT said no (${data.step}): ${data.error}`);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  const testConn = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/loadboard/dat/test");
      data.connected ? toast.success("DAT connection healthy ✅") : toast.error(`Test failed: ${data.error}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect DAT? The board goes back to sample loads.")) return;
    await client.delete("/loadboard/dat/connection");
    toast.success("DAT disconnected");
    onChanged();
  };

  if (conn === null) return null;

  if (conn.connected) {
    return (
      <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.05] p-4 flex flex-wrap items-center gap-3" data-testid="dat-connect-card">
        <span className="text-sm text-emerald-300 font-semibold">🟢 DAT One connected</span>
        <span className="text-xs text-neutral-400">as {conn.seat_user_email} · service {conn.service_email}</span>
        <div className="ml-auto flex gap-2">
          <button onClick={testConn} disabled={busy} data-testid="dat-test-btn"
            className="rounded-full px-3 py-1.5 text-xs border border-white/15 text-neutral-300 hover:border-emerald-400/50">
            {busy ? "Testing…" : "Test connection"}
          </button>
          <button onClick={disconnect} data-testid="dat-disconnect-btn"
            className="rounded-full px-3 py-1.5 text-xs text-neutral-500 hover:text-red-400">
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-surface p-4" data-testid="dat-connect-card">
      <button onClick={() => setOpen(!open)} data-testid="dat-connect-toggle"
        className="w-full flex items-center justify-between text-left">
        <div>
          <div className="text-sm font-semibold text-white">🔌 Connect your DAT account</div>
          <div className="text-xs text-neutral-500 mt-0.5">
            {conn.status === "error"
              ? <span className="text-red-400">Last attempt failed: {conn.last_error}</span>
              : "Have a DAT subscription? Plug in your API service account and this board goes live."}
          </div>
        </div>
        <span className="text-neutral-500 text-xs shrink-0">{open ? "▲ close" : "▼ open"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="text-xs text-neutral-400 leading-relaxed rounded-lg bg-white/[0.03] border border-white/10 p-3">
            <b className="text-neutral-200">One-time setup with DAT:</b> email{" "}
            <span className="text-[#FF9764]">techsupportteamleads@dat.com</span> from the account that owns
            your DAT subscription and ask to add <b>REST API (Connexion) access</b> + a <b>service account</b>.
            DAT sends you a service-account email + password. Paste them here with your normal DAT login email.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.service_email}
              onChange={(e) => setForm({ ...form, service_email: e.target.value })}
              placeholder="Service account email (from DAT)"
              data-testid="dat-service-email"
              className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/50" />
            <input type="password" value={form.service_password}
              onChange={(e) => setForm({ ...form, service_password: e.target.value })}
              placeholder="Service account password"
              data-testid="dat-service-password"
              className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/50" />
            <input value={form.seat_user_email}
              onChange={(e) => setForm({ ...form, seat_user_email: e.target.value })}
              placeholder="Your DAT login email (seat user)"
              data-testid="dat-seat-email"
              className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/50" />
          </div>
          <button onClick={connect} disabled={busy} data-testid="dat-connect-btn"
            className="rounded-full px-5 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-60">
            {busy ? "Verifying with DAT…" : "Connect & test"}
          </button>
        </div>
      )}
    </div>
  );
}

// Connect Truckstop Account — same treatment as DAT for Truckstop users.
function TruckstopConnectCard({ onChanged }) {
  const [conn, setConn] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", integration_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await client.get("/loadboard/truckstop/connection");
      setConn(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!form.username || !form.password || !form.integration_id) {
      toast.error("All three fields are needed");
      return;
    }
    setBusy(true);
    try {
      const { data } = await client.post("/loadboard/truckstop/connect", form);
      if (data.connected) {
        toast.success("Truckstop connected — your board is now LIVE 🎉");
        onChanged();
      } else {
        toast.error(`Truckstop said no (${data.step}): ${data.error}`);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Truckstop?")) return;
    await client.delete("/loadboard/truckstop/connection");
    toast.success("Truckstop disconnected");
    onChanged();
  };

  if (conn === null) return null;

  if (conn.connected) {
    return (
      <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.05] p-4 flex flex-wrap items-center gap-3" data-testid="truckstop-connect-card">
        <span className="text-sm text-emerald-300 font-semibold">🟢 Truckstop connected</span>
        <span className="text-xs text-neutral-400">as {conn.username}</span>
        <button onClick={disconnect} data-testid="truckstop-disconnect-btn"
          className="ml-auto rounded-full px-3 py-1.5 text-xs text-neutral-500 hover:text-red-400">
          Disconnect
        </button>
      </div>
    );
  }

  return (
    <div className="card-surface p-4" data-testid="truckstop-connect-card">
      <button onClick={() => setOpen(!open)} data-testid="truckstop-connect-toggle"
        className="w-full flex items-center justify-between text-left">
        <div>
          <div className="text-sm font-semibold text-white">🔌 Use Truckstop instead?</div>
          <div className="text-xs text-neutral-500 mt-0.5">
            {conn.status === "error"
              ? <span className="text-red-400">Last attempt failed: {conn.last_error}</span>
              : "Truckstop subscribers can connect their API credentials here."}
          </div>
        </div>
        <span className="text-neutral-500 text-xs shrink-0">{open ? "▲ close" : "▼ open"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="text-xs text-neutral-400 leading-relaxed rounded-lg bg-white/[0.03] border border-white/10 p-3">
            <b className="text-neutral-200">One-time setup with Truckstop:</b> ask your Truckstop rep
            (or call their support line) to enable <b>API / integration access</b> on your subscription.
            They'll give you an <b>Integration ID</b> to use with your Truckstop login.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="Truckstop username"
              data-testid="truckstop-username"
              className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/50" />
            <input type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Password"
              data-testid="truckstop-password"
              className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/50" />
            <input value={form.integration_id}
              onChange={(e) => setForm({ ...form, integration_id: e.target.value })}
              placeholder="Integration ID (from your rep)"
              data-testid="truckstop-integration-id"
              className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/50" />
          </div>
          <button onClick={connect} disabled={busy} data-testid="truckstop-connect-btn"
            className="rounded-full px-5 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-60">
            {busy ? "Verifying with Truckstop…" : "Connect & test"}
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, tone = "default" }) {
  const toneMap = { default: "text-white", good: "text-emerald-400", money: "text-[#FF5A1F]" };
  return (
    <div className="card-surface p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
        {icon}
        {label}
      </div>
      <div className={`font-display text-2xl font-semibold tracking-tight ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}


function MatchRow({ match, onBid, isBidding, repeatFlag, brokerSpeed }) {
  const tier = TIER_STYLE[match.ai_tier] || TIER_STYLE.marginal;
  const showRepeatWarning = repeatFlag && repeatFlag.alert;

  // Lane sentiment: when the pilot has won >= 2 loads on this broker+lane in
  // the last 90 days AND their avg won $/mi is 15%+ below the current posted
  // $/mi, this broker "underpays this lane" — surface it inline. Sample size
  // guard (>=2 wins) keeps the badge quiet until it's actually meaningful.
  const wonCount = repeatFlag?.won_count_90d || 0;
  const historicAvg = repeatFlag?.avg_won_per_mile;
  const postedPerMile = Number(match.rate_per_mile) || 0;
  let sentimentPct = null;
  if (wonCount >= 2 && historicAvg && postedPerMile > 0) {
    // How much below the posting price your historical avg sits — e.g. 22 → "22% below"
    const gap = ((postedPerMile - historicAvg) / postedPerMile) * 100;
    if (gap >= 15) {
      sentimentPct = Math.round(gap);
    }
  }
  const showLaneSentiment = sentimentPct !== null;
  return (
    <div className={`card-surface p-5 border ${tier.border}`} data-testid={`load-match-${match.match_id.slice(0, 8)}`}>
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
        {/* Score badge */}
        <div className="lg:w-24 shrink-0">
          <div className={`rounded-xl ${tier.bg} border ${tier.border} p-3 text-center`}>
            <div className={`text-3xl font-display font-semibold ${tier.text}`} data-testid={`match-score-${match.match_id.slice(0, 8)}`}>
              {Math.round(match.ai_score)}
            </div>
            <div className={`text-[10px] tracking-widest font-semibold ${tier.text} mt-0.5`}>{tier.label}</div>
          </div>
        </div>

        {/* Lane + broker */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <MapPin size={14} className="text-neutral-500 shrink-0" />
            <span className="text-white font-semibold text-base truncate" data-testid="match-lane">
              {match.origin_city}, {match.origin_state}
            </span>
            <ArrowRight size={12} className="text-neutral-600" />
            <span className="text-white font-semibold text-base truncate">
              {match.destination_city}, {match.destination_state}
            </span>
            {match.provider === "mock" && (
              <span
                className="shrink-0 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-[9px] font-bold tracking-widest px-2 py-0.5 uppercase"
                title="Sample load — practice data, not live freight"
                data-testid={`match-sample-chip-${match.match_id.slice(0, 8)}`}
              >
                Sample
              </span>
            )}
            {match.provider === "freightpilot" && (
              <span
                className="shrink-0 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 text-[9px] font-bold tracking-widest px-2 py-0.5 uppercase"
                title="Real load posted directly on FreightPilot — contact goes straight to the poster"
                data-testid={`match-live-chip-${match.match_id.slice(0, 8)}`}
              >
                ● Live
              </span>
            )}
            {match.provider === "freightpilot" && match.mc_verified && (
              <span
                className="shrink-0 rounded-full border border-sky-400/40 bg-sky-400/10 text-sky-300 text-[9px] font-bold tracking-widest px-2 py-0.5 uppercase"
                title="Poster's MC number checks out against FMCSA public records"
                data-testid={`match-mc-verified-${match.match_id.slice(0, 8)}`}
              >
                MC ✓
              </span>
            )}
            {showRepeatWarning && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-300"
                title={`${repeatFlag.count} bids with this broker on this lane in the last 7 days · 0 won · ${repeatFlag.lost} lost/auto-closed`}
                data-testid={`match-repeat-warning-${match.match_id.slice(0, 8)}`}
              >
                <Warning size={10} weight="fill" />
                {repeatFlag.count} bids · 0 wins this week
              </span>
            )}
            {showLaneSentiment && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-300"
                title={`Historical avg won: $${historicAvg.toFixed(2)}/mi vs $${postedPerMile.toFixed(2)}/mi posted (${wonCount} won in last 90 days). This broker typically underpays this lane.`}
                data-testid={`match-lane-sentiment-${match.match_id.slice(0, 8)}`}
              >
                <TrendUp size={10} weight="fill" className="rotate-180" />
                Underpays this lane · ~{sentimentPct}% below
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-1">
            {match.miles.toLocaleString()}mi · {match.deadhead_miles > 0 ? `${match.deadhead_miles}mi deadhead · ` : "no deadhead · "}
            {match.equipment} · {match.weight_lbs.toLocaleString()} lbs
          </div>
          <div className="text-xs text-neutral-400 mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-neutral-500">{match.broker_company}</span>
            {brokerSpeed && brokerSpeed.avg_reply_hours >= 48 ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-300"
                data-testid={`broker-slow-${match.match_id.slice(0, 8)}`}
                title={`Based on ${brokerSpeed.replies} answered bid${brokerSpeed.replies === 1 ? "" : "s"} — don't wait on them, keep bidding elsewhere`}
              >
                <Hourglass size={10} weight="fill" /> slow — usually {fmtReplyTime(brokerSpeed.avg_reply_hours)} to answer
              </span>
            ) : brokerSpeed ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-300"
                data-testid={`broker-speed-${match.match_id.slice(0, 8)}`}
                title={`Based on ${brokerSpeed.replies} answered bid${brokerSpeed.replies === 1 ? "" : "s"} with this broker`}
              >
                <Lightning size={10} weight="fill" /> answers in ~{fmtReplyTime(brokerSpeed.avg_reply_hours)}
              </span>
            ) : null}
            <span className="text-neutral-700">·</span>
            <span>{match.broker_contact}</span>
            {match.broker_phone && (
              <>
                <span className="text-neutral-700">·</span>
                <a
                  href={`tel:${match.broker_phone.replace(/[^+\d]/g, "")}`}
                  onClick={(e) => { e.stopPropagation(); logCall(match); }}
                  data-testid={`match-call-broker-${match.match_id.slice(0, 8)}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-emerald-300 font-semibold hover:bg-emerald-500/20 transition-colors"
                  title={`Call ${match.broker_company}`}
                >
                  <Phone size={12} weight="fill" />
                  {match.broker_phone}
                </a>
              </>
            )}
            <span className="text-neutral-700">·</span>
            <span className="inline-flex items-center gap-1 text-neutral-500">
              <Clock size={11} /> {fmtAge(match.age_min)}
            </span>
          </div>
          {match.ai_reasoning && (
            <div className="text-[11px] text-neutral-400 mt-2 italic" data-testid="match-reasoning">
              <Sparkle size={11} weight="fill" className="inline text-[#FF5A1F] mr-1" />
              {match.ai_reasoning}
            </div>
          )}
        </div>

        {/* Money */}
        <div className="lg:w-40 shrink-0 text-right">
          <div className="font-display text-2xl font-semibold text-white" data-testid="match-posted-rate">
            {fmtMoney(match.posted_rate)}
          </div>
          <div className="text-xs text-neutral-500">{`$${match.rate_per_mile.toFixed(2)}/mi posted`}</div>
          <div className="mt-2 pt-2 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-0.5">Ask</div>
            <div className="font-display text-lg font-semibold text-[#FF5A1F]" data-testid="match-target-rate">
              {fmtMoney(match.ai_target_rate)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={() => onBid(match)}
          disabled={isBidding}
          data-testid={`match-bid-btn-${match.match_id.slice(0, 8)}`}
          className="lg:w-32 shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5A1F] text-black font-semibold py-2.5 text-sm hover:bg-[#FF7038] transition-colors disabled:opacity-50 disabled:cursor-wait"
        >
          <Envelope size={14} weight="bold" />
          {isBidding ? "Drafting…" : "Draft Bid"}
        </button>
      </div>
    </div>
  );
}


function HotStreakBadge() {
  const [wins, setWins] = useState(0);
  useEffect(() => {
    client.get("/loadboard/analytics")
      .then((r) => { if (r.data.hot_streak) setWins(r.data.wins_this_week); })
      .catch(() => {});
  }, []);
  if (!wins) return null;
  return (
    <div data-testid="hot-streak-badge-board" className="flex items-center gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
      <span className="text-xl">🔥</span>
      <div>
        <div className="text-sm font-bold text-amber-300">Hot streak — {wins} wins this week</div>
        <div className="text-[11px] text-amber-500/80">3+ booked loads this week. Keep it rolling!</div>
      </div>
    </div>
  );
}

function ReplyRateCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    client.get("/loadboard/reply-rate").then((res) => setData(res.data)).catch(() => setData(null));
  }, []);
  if (!data || !data.all_time || data.all_time.sent_total === 0) return null;
  const at = data.all_time;
  const m30 = data.last_30d;
  return (
    <div className="card-surface p-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="reply-rate-card">
      <div className="flex items-center gap-2">
        <Envelope size={16} weight="fill" className="text-[#FF5A1F]" />
        <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Reply Rate</span>
      </div>
      <div className="font-display text-xl font-semibold text-white" data-testid="reply-rate-pct">
        {at.reply_rate_pct === null ? "—" : `${at.reply_rate_pct}%`}
        <span className="text-xs font-normal text-neutral-500 ml-1.5">of closed bids answered</span>
      </div>
      <div className="text-xs text-neutral-400 flex items-center gap-3 flex-wrap">
        <span className="text-emerald-300">{at.answered} answered</span>
        <span className="text-neutral-600">·</span>
        <span className="text-red-300">{at.ghosted} ghosted</span>
        <span className="text-neutral-600">·</span>
        <span className="text-neutral-400">{at.pending} pending</span>
        {m30.reply_rate_pct !== null && (
          <>
            <span className="text-neutral-600">·</span>
            <span className="text-[#FF5A1F] font-semibold" data-testid="reply-rate-30d">
              Last 30 days: {m30.reply_rate_pct}%
            </span>
          </>
        )}
      </div>
    </div>
  );
}


function fmtReplyTime(hours) {
  if (hours < 1) return `${Math.max(Math.round(hours * 60), 1)}m`;
  if (hours < 48) return `${Math.round(hours * 10) / 10}h`;
  return `${Math.round(hours / 24)}d`;
}


function FastestBrokersCard({ brokers }) {
  const [open, setOpen] = useState(false);
  const [watched, setWatched] = useState(new Set());
  const [busyBroker, setBusyBroker] = useState(null);
  useEffect(() => {
    client.get("/loadboard-watchers").then((r) => {
      setWatched(new Set((r.data.watchers || []).map((w) => w.broker)));
    }).catch(() => {});
  }, []);
  const watchBroker = async (name) => {
    setBusyBroker(name);
    try {
      await client.post("/loadboard-watchers", { broker: name });
      setWatched((prev) => new Set([...prev, name]));
      toast.success(`👁 Watching ${name} — you'll get pinged the moment they post a matching load`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create watcher");
    } finally {
      setBusyBroker(null);
    }
  };
  const top = open ? brokers : brokers.slice(0, 3);
  return (
    <div className="card-surface p-4" data-testid="fastest-brokers-card">
      <div className="flex items-center gap-2 mb-3">
        <Lightning size={15} weight="fill" className="text-amber-400" />
        <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Fastest Brokers</span>
        <span className="text-[11px] text-neutral-600">ranked by how fast they answer your bids</span>
        {brokers.length > 3 && (
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="fastest-brokers-toggle"
            className="ml-auto text-[11px] text-neutral-400 hover:text-white transition-colors"
          >
            {open ? "Show less" : `Show all ${brokers.length}`}
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {top.map((b, i) => (
          <div
            key={b.broker_company}
            className="flex items-center gap-3 rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2"
            data-testid={`fastest-broker-row-${i}`}
          >
            <span className={`w-6 text-center font-display font-bold text-sm ${i === 0 ? "text-amber-300" : i === 1 ? "text-neutral-300" : "text-neutral-500"}`}>
              #{i + 1}
            </span>
            <span className="text-sm text-white font-medium flex-1 min-w-0 truncate">{b.broker_company}</span>
            {b.avg_reply_hours >= 48 ? (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-300" title="Usually 2+ days to answer — don't wait on them">
                <Hourglass size={11} weight="fill" /> ~{fmtReplyTime(b.avg_reply_hours)} · SLOW
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-300">
                <Lightning size={11} weight="fill" /> ~{fmtReplyTime(b.avg_reply_hours)}
              </span>
            )}
            <span className="text-[11px] text-neutral-500 w-20 text-right">{b.replies} replies</span>
            <span className={`text-[11px] font-semibold w-16 text-right ${b.win_rate_pct >= 50 ? "text-emerald-300" : "text-neutral-400"}`}>
              {b.win_rate_pct}% won
            </span>
            {watched.has(b.broker_company) ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold text-emerald-300 shrink-0"
                data-testid={`broker-watching-${i}`}
              >
                <BellRinging size={11} weight="fill" /> Watching
              </span>
            ) : (
              <button
                onClick={() => watchBroker(b.broker_company)}
                disabled={busyBroker === b.broker_company}
                data-testid={`broker-watch-btn-${i}`}
                className="inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[10px] font-semibold text-neutral-300 hover:border-[#FF5A1F]/50 hover:text-[#FF5A1F] transition-colors disabled:opacity-50 shrink-0"
              >
                <Bell size={11} /> {busyBroker === b.broker_company ? "…" : "Watch"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function logCall(match) {
  client.post("/loadboard/call-log", {
    match_id: match.match_id,
    broker_company: match.broker_company,
    origin: `${match.origin_city}, ${match.origin_state}`,
    destination: `${match.destination_city}, ${match.destination_state}`,
    posted_rate: match.posted_rate,
    ai_target_rate: match.ai_target_rate,
    miles: match.miles,
    equipment: match.equipment,
    broker_phone: match.broker_phone || "",
  }).then((r) => {
    if (r.data.status === "logged") {
      toast.success("📞 Call logged — mark it Won in Bid Analytics when you book it");
    }
  }).catch(() => {});
}


function DraftBidModal({ state, onUpdateBody, onUpdateSubject, onSend, onClose }) {
  const { match, draft, sending } = state;
  const [brokerStats, setBrokerStats] = useState(null);
  const [brokerLoading, setBrokerLoading] = useState(true);

  // Fetch historical win-rate with THIS specific broker the moment the modal opens
  useEffect(() => {
    let alive = true;
    const brokerName = match?.broker_company;
    if (!brokerName) { setBrokerLoading(false); return; }
    setBrokerLoading(true);
    client
      .get(`/loadboard/broker-stats?broker_company=${encodeURIComponent(brokerName)}`)
      .then((r) => alive && setBrokerStats(r.data))
      .catch(() => alive && setBrokerStats(null))
      .finally(() => alive && setBrokerLoading(false));
    return () => { alive = false; };
  }, [match?.broker_company]);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && !sending && onClose()}
      data-testid="draft-bid-modal"
    >
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-[#0A0A0A] border border-white/10 rounded-2xl flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={14} weight="fill" className="text-[#FF5A1F]" />
              <span className="overline text-[#FF5A1F]">DRAFT BID REVIEW</span>
            </div>
            <h3 className="font-display text-lg font-semibold text-white truncate">
              {match.origin_city}, {match.origin_state} → {match.destination_city}, {match.destination_state}
            </h3>
            <div className="text-xs text-neutral-500 mt-0.5">
              To: {match.broker_contact} &lt;{match.broker_email}&gt; · {match.broker_company}
            </div>
            {match.broker_phone && (
              <a
                href={`tel:${match.broker_phone.replace(/[^+\d]/g, "")}`}
                onClick={() => logCall(match)}
                data-testid="draft-modal-call-broker"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors"
              >
                <Phone size={13} weight="fill" />
                Or call them: {match.broker_phone}
              </a>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            data-testid="draft-modal-close"
            className="text-neutral-500 hover:text-white transition-colors p-1 shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Broker relationship context strip */}
        {!brokerLoading && brokerStats && (
          <BrokerContextStrip stats={brokerStats} brokerName={match.broker_company} />
        )}

        {/* Repeat-bids warning — fires only when 3+ bids in a week with 0 wins */}
        <RepeatBidsAlert
          brokerCompany={match.broker_company}
          originState={match.origin_state}
          destinationState={match.destination_state}
        />

        {/* Lane pricing intelligence — P25/Avg/P75 for this corridor */}
        <div className="px-5 pt-3 pb-1">
          <LanePricingCard
            originState={match.origin_state}
            destState={match.destination_state}
            equipment={match.equipment}
          />
        </div>

        {/* Rate summary */}
        <div className="p-5 border-b border-white/5 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-[10px] text-neutral-500 tracking-widest mb-1">POSTED</div>
            <div className="font-display text-lg font-semibold text-neutral-300">{fmtMoney(match.posted_rate)}</div>
          </div>
          <div>
            <div className="text-[10px] text-[#FF5A1F] tracking-widest mb-1">YOUR ASK</div>
            <div className="inline-flex items-center gap-1.5">
              <div className="font-display text-lg font-semibold text-[#FF5A1F]" data-testid="draft-modal-your-ask">
                {fmtMoney(draft.target_rate || match.ai_target_rate)}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  const amount = Math.round(draft.target_rate || match.ai_target_rate);
                  navigator.clipboard.writeText(String(amount))
                    .then(() => toast.success(`Copied $${amount.toLocaleString()} to clipboard`))
                    .catch(() => toast.error("Copy failed — try selecting manually"));
                  e.currentTarget.blur();
                }}
                data-testid="draft-modal-copy-ask"
                title="Copy amount for quotes over phone/text"
                className="text-neutral-500 hover:text-[#FF5A1F] transition-colors p-1 rounded-md hover:bg-[#FF5A1F]/10"
              >
                <Copy size={12} weight="bold" />
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-neutral-500 tracking-widest mb-1">AI ANCHOR</div>
            <div className="font-display text-lg font-semibold text-white">{fmtMoney(draft.anchor_rate || match.ai_target_rate)}</div>
          </div>
        </div>

        {/* Editable draft */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Rejection-learning badge — AI factored the broker's prior rejections into the anchor */}
          {draft.rejection_history && (draft.rejection_history.rejection_count || 0) >= 2 && (
            <div
              data-testid="draft-modal-rejection-learning"
              className="rounded-lg border border-indigo-500/40 bg-indigo-500/10 p-3"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-indigo-300">
                <Sparkle size={11} weight="fill" />
                AI Learned From {draft.rejection_history.rejection_count} Prior Rejection{draft.rejection_history.rejection_count === 1 ? "" : "s"}
              </div>
              <div className="text-xs mt-1 text-indigo-100/90">
                Anchoring near this broker's historical ceiling of{" "}
                <b>${Number(draft.rejection_history.typical_ceiling_per_mile || 0).toFixed(2)}/mi</b>
                {draft.rejection_history.no_response_count > 0 && (
                  <> · <span className="text-indigo-200/80">{draft.rejection_history.no_response_count} ghosted</span></>
                )}
                {(draft.rejection_history.same_lane_rejection_count || 0) >= 2 && draft.rejection_history.same_lane_avg_ask_per_mile && (
                  <> · Same-lane avg <b>${Number(draft.rejection_history.same_lane_avg_ask_per_mile).toFixed(2)}/mi</b></>
                )}
              </div>
            </div>
          )}

          {/* Wins-anchor badge — AI factored the broker's proven willingness-to-pay floor */}
          {draft.wins_history && (draft.wins_history.win_count || 0) >= 1 && (
            <div
              data-testid="draft-modal-wins-anchor"
              className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-emerald-300">
                <Sparkle size={11} weight="fill" />
                AI Learned From {draft.wins_history.win_count} Prior Win{draft.wins_history.win_count === 1 ? "" : "s"}
              </div>
              <div className="text-xs mt-1 text-emerald-100/90">
                {draft.wins_history.win_count >= 2 ? (
                  <>Proven floor <b>${Number(draft.wins_history.confident_floor_per_mile || 0).toFixed(2)}/mi</b></>
                ) : (
                  <>Won at <b>${Number(draft.wins_history.avg_won_per_mile || 0).toFixed(2)}/mi</b></>
                )}
                {" · Best "}<b>${Number(draft.wins_history.best_won_per_mile || 0).toFixed(2)}/mi</b>
                {(draft.wins_history.same_lane_win_count || 0) >= 1 && draft.wins_history.same_lane_avg_won_per_mile && (
                  <> · Same-lane avg <b>${Number(draft.wins_history.same_lane_avg_won_per_mile).toFixed(2)}/mi</b></>
                )}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] text-neutral-500 tracking-widest uppercase">Subject</label>
            <input
              type="text"
              value={draft.subject}
              onChange={(e) => onUpdateSubject(e.target.value)}
              disabled={sending}
              data-testid="draft-modal-subject"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 tracking-widest uppercase flex items-center gap-1">
              Body <PencilSimple size={10} /> <span className="normal-case text-neutral-600 tracking-normal">— edit before sending</span>
            </label>
            <textarea
              value={draft.body}
              onChange={(e) => onUpdateBody(e.target.value)}
              disabled={sending}
              rows={13}
              data-testid="draft-modal-body"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none leading-relaxed font-mono resize-y"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-white/10 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            disabled={sending}
            data-testid="draft-modal-cancel"
            className="text-sm text-neutral-400 hover:text-white transition-colors px-3 py-2"
          >
            Cancel
          </button>
          <button
            onClick={onSend}
            disabled={sending || !draft.body.trim()}
            data-testid="draft-modal-send"
            className="inline-flex items-center gap-2 rounded-full bg-[#FF5A1F] text-black font-semibold py-2.5 px-6 text-sm hover:bg-[#FF7038] transition-colors disabled:opacity-50 disabled:cursor-wait"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {sending ? "Sending…" : `Send to ${match.broker_email.split("@")[0]}`}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Broker relationship context — sits between the modal header and the rate
 * summary. Shows the pilot's historical win-rate with THIS specific broker
 * (or a "first bid" state when there's no history).
 */
function BrokerContextStrip({ stats, brokerName }) {
  const closed = stats.closed || 0;
  const won = stats.won || 0;
  const pending = stats.pending || 0;
  const total = stats.total_bids || 0;

  // First-bid state — never bid this broker before
  if (total === 0) {
    return (
      <div
        className="px-5 py-3 border-b border-white/5 bg-white/[0.015] flex items-center gap-3"
        data-testid="broker-context-first"
      >
        <Handshake size={16} className="text-neutral-500 shrink-0" />
        <div className="text-xs text-neutral-400">
          <span className="text-white font-medium">First bid</span> with {brokerName}. No relationship history yet — make it count.
        </div>
      </div>
    );
  }

  // Rich history with closed bids — show win rate
  const winRate = stats.win_rate_pct;
  const winRateTone =
    winRate === null ? "text-neutral-400" :
    winRate >= 75 ? "text-emerald-400" :
    winRate >= 40 ? "text-[#FF5A1F]" :
    "text-amber-400";
  const barTone =
    winRate === null ? "bg-neutral-500" :
    winRate >= 75 ? "bg-emerald-400" :
    winRate >= 40 ? "bg-[#FF5A1F]" :
    "bg-amber-400";

  const last = stats.last_won;
  const lastWonDate = last?.closed_at ? new Date(last.closed_at) : null;
  const lastWonShort = lastWonDate && !isNaN(lastWonDate) ? lastWonDate.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;

  return (
    <div className="px-5 py-3 border-b border-white/5 bg-white/[0.015]" data-testid="broker-context-history">
      <div className="flex items-center gap-3 flex-wrap">
        <Trophy size={16} weight={winRate !== null && winRate >= 75 ? "fill" : "regular"} className={`shrink-0 ${winRateTone}`} />
        <div className="flex-1 min-w-0 text-xs">
          <div className="text-white">
            You've closed <b className="tabular-nums">{won}/{closed}</b> with {brokerName}
            {winRate !== null && (
              <> — <span className={`${winRateTone} font-semibold`}>{winRate.toFixed(0)}% win rate</span></>
            )}
            {closed === 0 && pending > 0 && (
              <> — <span className="text-neutral-400">{pending} bid{pending === 1 ? "" : "s"} still open</span></>
            )}
          </div>
          {last && lastWonShort && (
            <div className="text-neutral-500 mt-0.5" data-testid="broker-context-last-won">
              Last won: <b className="text-neutral-300">${Math.round(last.final_rate).toLocaleString()}</b>
              {last.per_mile && <> ({last.per_mile.toFixed(2)}/mi)</>}
              <span className="text-neutral-700"> · </span>
              {lastWonShort}
            </div>
          )}
        </div>
        {/* Win-rate bar (only when we have closed history) */}
        {winRate !== null && (
          <div className="hidden sm:block w-24 shrink-0" title={`${winRate.toFixed(0)}% win rate`}>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className={`h-full ${barTone} transition-all`} style={{ width: `${Math.min(100, winRate)}%` }} />
            </div>
            <div className={`text-[9px] mt-0.5 tracking-wider text-right ${winRateTone}`}>
              {winRate.toFixed(0)}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
